import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import RefreshIcon from '@mui/icons-material/Refresh'
import BlockIcon from '@mui/icons-material/Block'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { equipeService } from '@/services/equipeService'
import { permissoesGestaoComercial } from '@/utils/gestaoComercial'
import { CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import { AUTOR_PAPEL_ROTULO, AlteracaoGestao, rotuloAlteracao } from '@/types/campanha'
import {
  CandidatoEquipe,
  Equipe,
  MembroEquipe,
  NOME_EQUIPE_MAX,
  TipoCandidatoEquipe,
  mensagemErroEquipe,
} from '@/types/equipe'

const formatDate = (iso?: string | null) => {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

const formatDataHora = (iso?: string | null) => {
  if (!iso) return '—'
  return `${formatDate(iso)}${iso.length > 15 ? ` ${iso.slice(11, 16)}` : ''}`
}

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

const rotuloCanal = (canal?: string | null) =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : 'Sem canal'

const abreviar = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id)

const rotuloCandidato = (c: CandidatoEquipe) =>
  `${c.nome || c.email || abreviar(c.usuarioId)}${c.canal ? ` · ${rotuloCanal(c.canal)}` : ''}`

type FiltroAtiva = 'ativas' | 'todas'

/**
 * Busca de candidatos na projeção de usuários (`/v1/equipes/candidatos`, ADMIN), com o texto
 * indo ao servidor. Mantém a última lista enquanto o usuário digita.
 */
const useCandidatos = (tipo: TipoCandidatoEquipe, ativo: boolean) => {
  const [busca, setBusca] = useState('')
  const [opcoes, setOpcoes] = useState<CandidatoEquipe[]>([])
  const [carregando, setCarregando] = useState(false)
  useEffect(() => {
    if (!ativo) return
    let vivo = true
    const t = setTimeout(
      () => {
        setCarregando(true)
        equipeService
          .candidatos(tipo, busca)
          .then((l) => vivo && setOpcoes(l))
          .catch(() => vivo && setOpcoes([]))
          .finally(() => vivo && setCarregando(false))
      },
      busca ? 300 : 0
    )
    return () => {
      vivo = false
      clearTimeout(t)
    }
  }, [tipo, busca, ativo])
  return { busca, setBusca, opcoes, carregando }
}

/**
 * Equipes do GERENTE (AN-8 §1; menu "Equipes" ao lado de Campanhas). ADMIN: lista, cria,
 * edita (nome/gerente), desativa e gere membros. GERENTE com GESTOR_EQUIPE: vê só a própria
 * equipe, em leitura. Demais: sem permissão (a tela nem chama o backend).
 */
const EquipesPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const perm = useMemo(() => permissoesGestaoComercial(usuario?.permissoes), [usuario])
  const podeVer = perm.geriParticipantes
  const ehAdmin = perm.ehAdmin

  const [filtro, setFiltro] = useState<FiltroAtiva>('ativas')
  const [equipes, setEquipes] = useState<Equipe[]>([])
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>('carregando')

  const [dialogo, setDialogo] = useState<{ aberto: boolean; editando: Equipe | null }>({
    aberto: false,
    editando: null,
  })
  const [nome, setNome] = useState('')
  const [gerente, setGerente] = useState<CandidatoEquipe | null>(null)
  const [salvando, setSalvando] = useState(false)
  const gerentes = useCandidatos('GERENTE', dialogo.aberto && ehAdmin)

  const [detalhe, setDetalhe] = useState<Equipe | null>(null)

  const carregar = useCallback(async () => {
    if (!podeVer) return
    setEstado('carregando')
    try {
      const lista = await equipeService.listar(filtro === 'ativas' ? true : undefined)
      setEquipes(lista)
      setEstado(lista.length === 0 ? 'vazio' : null)
    } catch (e) {
      setEquipes([])
      setEstado(estadoDoErro(e))
    }
  }, [filtro, podeVer])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrirNova = () => {
    setNome('')
    setGerente(null)
    setDialogo({ aberto: true, editando: null })
  }

  const abrirEdicao = (eq: Equipe) => {
    setNome(eq.nome)
    setGerente({ usuarioId: eq.gerente.id, nome: eq.gerente.nome, equipeId: eq.id })
    setDialogo({ aberto: true, editando: eq })
  }

  const fecharDialogo = () => setDialogo({ aberto: false, editando: null })

  const salvar = async () => {
    const n = nome.trim()
    if (!n) {
      enqueueSnackbar('Nome é obrigatório', { variant: 'warning' })
      return
    }
    if (!gerente) {
      enqueueSnackbar('Escolha o gerente da equipe', { variant: 'warning' })
      return
    }
    setSalvando(true)
    try {
      const req = { nome: n, gerenteId: gerente.usuarioId }
      if (dialogo.editando) await equipeService.atualizar(dialogo.editando.id, req)
      else await equipeService.criar(req)
      enqueueSnackbar(dialogo.editando ? 'Equipe atualizada' : 'Equipe criada', { variant: 'success' })
      fecharDialogo()
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroEquipe(e, 'Falha ao salvar a equipe'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const desativar = async (eq: Equipe) => {
    if (!window.confirm(`Desativar a equipe "${eq.nome}"? Os membros saem da equipe.`)) return
    try {
      await equipeService.desativar(eq.id)
      enqueueSnackbar('Equipe desativada', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroEquipe(e, 'Falha ao desativar a equipe'), { variant: 'error' })
    }
  }

  if (!podeVer) {
    return (
      <Box>
        <PageHeader title="Equipes" subtitle="Equipes dos gerentes para campanhas, metas e ranking" />
        <Paper>
          <EstadoDeLista estado="semPermissao" minHeight={240} />
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Equipes"
        subtitle="Equipes dos gerentes para campanhas, metas e ranking"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void carregar()}>
              Atualizar
            </Button>
            {ehAdmin && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
                Nova equipe
              </Button>
            )}
          </Stack>
        }
      />

      {perm.ehGestorEquipe && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Você vê a sua equipe. A composição é mantida pelo administrador.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Situação"
          select
          size="small"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value as FiltroAtiva)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ativas">Ativas</MenuItem>
          <MenuItem value="todas">Todas</MenuItem>
        </TextField>
      </Paper>

      <Paper>
        {estado ? (
          <EstadoDeLista
            estado={estado}
            minHeight={200}
            mensagem={estado === 'vazio' ? 'Nenhuma equipe cadastrada' : undefined}
            {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
          />
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Equipes">
              <TableHead>
                <TableRow>
                  <TableCell><strong>EQUIPE</strong></TableCell>
                  <TableCell><strong>GERENTE</strong></TableCell>
                  <TableCell><strong>MEMBROS</strong></TableCell>
                  <TableCell><strong>SITUAÇÃO</strong></TableCell>
                  <TableCell><strong>CRIADA EM</strong></TableCell>
                  <TableCell align="right"><strong>AÇÕES</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {equipes.map((eq) => (
                  <TableRow key={eq.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {eq.nome}
                      </Typography>
                    </TableCell>
                    <TableCell>{eq.gerente.nome || abreviar(eq.gerente.id)}</TableCell>
                    <TableCell>{eq.membros}</TableCell>
                    <TableCell>
                      <Chip size="small" label={eq.ativa ? 'Ativa' : 'Inativa'} color={eq.ativa ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>{formatDate(eq.criadoEm)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="text" onClick={() => setDetalhe(eq)}>
                        Abrir →
                      </Button>
                      {ehAdmin && eq.ativa && (
                        <Tooltip title="Editar">
                          <IconButton size="small" aria-label={`Editar ${eq.nome}`} onClick={() => abrirEdicao(eq)}>
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {ehAdmin && eq.ativa && (
                        <Tooltip title="Desativar">
                          <IconButton size="small" aria-label={`Desativar ${eq.nome}`} onClick={() => void desativar(eq)}>
                            <BlockIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <EquipeDetalhe
        equipe={detalhe}
        podeEditar={ehAdmin && !!detalhe?.ativa}
        onClose={() => setDetalhe(null)}
        onAlterou={() => void carregar()}
      />

      <Dialog open={dialogo.aberto} onClose={fecharDialogo} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
          {dialogo.editando ? 'Editar equipe' : 'Nova equipe'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              inputProps={{ maxLength: NOME_EQUIPE_MAX }}
              required
              fullWidth
            />
            <Autocomplete
              options={gerentes.opcoes}
              value={gerente}
              loading={gerentes.carregando}
              inputValue={gerentes.busca}
              onInputChange={(_, v, motivo) => motivo !== 'reset' && gerentes.setBusca(v)}
              onChange={(_, v) => setGerente(v)}
              filterOptions={(x) => x}
              getOptionLabel={rotuloCandidato}
              isOptionEqualToValue={(a, b) => a.usuarioId === b.usuarioId}
              // 1 equipe ativa por gerente (409 GERENTE_JA_TEM_EQUIPE): quem já tem fica desabilitado.
              getOptionDisabled={(c) => !!c.equipeId && c.equipeId !== dialogo.editando?.id}
              renderOption={(props, c) => (
                <li {...props} key={c.usuarioId}>
                  {rotuloCandidato(c)}
                  {c.equipeId && c.equipeId !== dialogo.editando?.id ? ' (já tem equipe)' : ''}
                </li>
              )}
              noOptionsText="Nenhum gerente encontrado"
              renderInput={(params) => (
                <TextField {...params} label="Gerente" required placeholder="Buscar gerente" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fecharDialogo} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void salvar()}
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

type AbaEquipe = 'membros' | 'historico'

const EquipeDetalhe: FC<{
  equipe: Equipe | null
  podeEditar: boolean
  onClose: () => void
  onAlterou: () => void
}> = ({ equipe, podeEditar, onClose, onAlterou }) => {
  const { enqueueSnackbar } = useSnackbar()
  const equipeId = equipe?.id
  const [aba, setAba] = useState<AbaEquipe>('membros')
  const [membros, setMembros] = useState<MembroEquipe[]>([])
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>(null)
  const [historico, setHistorico] = useState<AlteracaoGestao[] | null>(null)
  const [estadoHist, setEstadoHist] = useState<EstadoDeListaTipo | null>(null)
  const [novos, setNovos] = useState<CandidatoEquipe[]>([])
  const [salvando, setSalvando] = useState(false)
  const [removendo, setRemovendo] = useState<string | null>(null)
  const consultores = useCandidatos('CONSULTOR', !!equipeId && podeEditar)

  const carregarMembros = useCallback(async () => {
    if (!equipeId) return
    setEstado('carregando')
    try {
      setMembros(await equipeService.membros(equipeId))
      setEstado(null)
    } catch (e) {
      setEstado(estadoDoErro(e))
    }
  }, [equipeId])

  const carregarHistorico = useCallback(async () => {
    if (!equipeId) return
    setEstadoHist('carregando')
    try {
      setHistorico(await equipeService.alteracoes(equipeId))
      setEstadoHist(null)
    } catch (e) {
      setEstadoHist(estadoDoErro(e))
    }
  }, [equipeId])

  useEffect(() => {
    if (!equipeId) return
    setAba('membros')
    setNovos([])
    setHistorico(null)
    setEstadoHist(null)
    void carregarMembros()
  }, [equipeId, carregarMembros])

  useEffect(() => {
    if (aba === 'historico' && historico === null && estadoHist === null) void carregarHistorico()
  }, [aba, historico, estadoHist, carregarHistorico])

  const adicionar = async () => {
    if (!equipeId || novos.length === 0) return
    setSalvando(true)
    try {
      await equipeService.adicionarMembros(
        equipeId,
        novos.map((c) => c.usuarioId)
      )
      enqueueSnackbar(`${novos.length} membro(s) incluído(s)`, { variant: 'success' })
      setNovos([])
      setHistorico(null)
      await carregarMembros()
      onAlterou()
    } catch (e) {
      enqueueSnackbar(mensagemErroEquipe(e, 'Não foi possível incluir os membros'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const remover = async (m: MembroEquipe) => {
    if (!equipeId) return
    setRemovendo(m.consultorId)
    try {
      await equipeService.removerMembro(equipeId, m.consultorId)
      enqueueSnackbar('Membro removido', { variant: 'success' })
      setHistorico(null)
      await carregarMembros()
      onAlterou()
    } catch (e) {
      enqueueSnackbar(mensagemErroEquipe(e, 'Não foi possível remover o membro'), { variant: 'error' })
    } finally {
      setRemovendo(null)
    }
  }

  const nomeMembro = (id: string) => membros.find((m) => m.consultorId === id)?.nome || abreviar(id)

  const abaMembros = () => (
    <Stack spacing={2}>
      {podeEditar && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={consultores.opcoes}
            value={novos}
            loading={consultores.carregando}
            inputValue={consultores.busca}
            onInputChange={(_, v, motivo) => motivo !== 'reset' && consultores.setBusca(v)}
            onChange={(_, v) => setNovos(v)}
            filterOptions={(x) => x}
            getOptionLabel={rotuloCandidato}
            isOptionEqualToValue={(a, b) => a.usuarioId === b.usuarioId}
            // consultor em no máximo 1 equipe ativa (409 CONSULTOR_JA_EM_EQUIPE)
            getOptionDisabled={(c) => !!c.equipeId}
            renderOption={(props, c) => (
              <li {...props} key={c.usuarioId}>
                {rotuloCandidato(c)}
                {c.equipeId ? (c.equipeId === equipeId ? ' (já é membro)' : ' (em outra equipe)') : ''}
              </li>
            )}
            noOptionsText="Nenhum consultor encontrado"
            renderInput={(params) => <TextField {...params} label="Adicionar consultores" placeholder="Buscar consultor" />}
          />
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            disabled={novos.length === 0 || salvando}
            onClick={() => void adicionar()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Adicionar
          </Button>
        </Stack>
      )}
      {estado ? (
        <EstadoDeLista
          estado={estado}
          minHeight={160}
          {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregarMembros() } } : {})}
        />
      ) : membros.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Equipe sem membros" minHeight={160} />
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Membros da equipe">
            <TableHead>
              <TableRow>
                <TableCell>Consultor</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Incluído em</TableCell>
                {podeEditar && <TableCell align="right">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {membros.map((m) => (
                <TableRow key={m.consultorId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.nome || abreviar(m.consultorId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={rotuloCanal(m.canal)} />
                  </TableCell>
                  <TableCell>{formatDate(m.incluidoEm)}</TableCell>
                  {podeEditar && (
                    <TableCell align="right">
                      <Tooltip title="Remover da equipe">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remover ${m.nome || abreviar(m.consultorId)}`}
                            disabled={removendo === m.consultorId}
                            onClick={() => void remover(m)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )

  const abaHistorico = () => {
    if (estadoHist) {
      return (
        <EstadoDeLista
          estado={estadoHist}
          minHeight={160}
          {...(estadoHist === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregarHistorico() } } : {})}
        />
      )
    }
    if (!historico || historico.length === 0) {
      return <EstadoDeLista estado="vazio" mensagem="Nenhuma alteração registrada" minHeight={160} />
    }
    return (
      <TableContainer>
        <Table size="small" aria-label="Histórico da equipe">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>Alteração</TableCell>
              <TableCell>De → Para</TableCell>
              <TableCell>Autor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historico.map((h) => (
              <TableRow key={h.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDataHora(h.criadoEm)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rotuloAlteracao(h.tipo)}
                  </Typography>
                  {h.consultorId && (
                    <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                      {nomeMembro(h.consultorId)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {h.valorAnterior ?? '—'} → {h.valorNovo ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>{AUTOR_PAPEL_ROTULO[h.autorPapel] ?? h.autorPapel}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={!!equipe}
      onClose={onClose}
      PaperProps={{ role: 'dialog', 'aria-label': 'Detalhe da equipe', sx: { width: { xs: '100%', sm: 560 }, p: 3 } }}
    >
      {equipe && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="overline" sx={{ color: tokens.brandPrimary, fontWeight: 700 }}>
                Equipe
              </Typography>
              <Typography variant="h6">{equipe.nome}</Typography>
              <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                Gerente: {equipe.gerente.nome || abreviar(equipe.gerente.id)}
              </Typography>
            </Box>
            <IconButton aria-label="Fechar" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {!equipe.ativa && (
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
              Equipe desativada
            </Alert>
          )}
          <Tabs value={aba} onChange={(_, v: AbaEquipe) => setAba(v)} variant="fullWidth">
            <Tab value="membros" label="Membros" />
            <Tab value="historico" label="Histórico" />
          </Tabs>
          {aba === 'membros' ? abaMembros() : abaHistorico()}
        </Stack>
      )}
    </Drawer>
  )
}

export default EquipesPage
